import { Sparkles, Award, ShieldCheck } from "lucide-react";

const TIER_STYLES = {
  Bronze: {
    ribbon: "bg-gradient-to-r from-amber-500 to-orange-500",
    seal: "bg-amber-500",
    accent: "text-amber-600",
    accentBg: "bg-amber-500/10",
  },
  Silver: {
    ribbon: "bg-gradient-to-r from-slate-400 to-slate-500",
    seal: "bg-slate-400",
    accent: "text-slate-600",
    accentBg: "bg-slate-400/10",
  },
  Gold: {
    ribbon: "bg-gradient-to-r from-yellow-400 to-amber-500",
    seal: "bg-yellow-500",
    accent: "text-yellow-700",
    accentBg: "bg-yellow-500/10",
  },
};

export default function CertificateCard({ cert, compact = false }) {
  const t = TIER_STYLES[cert.tier] || TIER_STYLES.Bronze;

  return (
    <div
      data-testid={`cert-card-${cert.id}`}
      className="relative w-full max-w-3xl mx-auto bg-white rounded-[2rem] overflow-hidden border-2 border-border/50 shadow-2xl shadow-primary/5"
    >
      {/* Top ribbon */}
      <div className={`h-2 w-full ${t.ribbon}`} />

      {/* Decorative corners */}
      <div className="absolute top-6 left-6 w-16 h-16 border-l-2 border-t-2 border-primary/30 rounded-tl-2xl" />
      <div className="absolute top-6 right-6 w-16 h-16 border-r-2 border-t-2 border-primary/30 rounded-tr-2xl" />
      <div className="absolute bottom-6 left-6 w-16 h-16 border-l-2 border-b-2 border-primary/30 rounded-bl-2xl" />
      <div className="absolute bottom-6 right-6 w-16 h-16 border-r-2 border-b-2 border-primary/30 rounded-br-2xl" />

      {/* Watermark seal */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
        <Sparkles size={480} strokeWidth={0.5} />
      </div>

      <div className="relative p-8 sm:p-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground font-semibold mb-4">
            <Award size={12} className={t.accent} /> NeighborNet AI
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            Certificate of Kindness
          </h2>
          <div className={`inline-flex items-center gap-2 mt-3 rounded-full px-4 py-1 text-xs font-bold tracking-widest uppercase ${t.accentBg} ${t.accent}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${t.seal}`} /> {cert.tier} Tier
          </div>
        </div>

        {/* Recipient */}
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Presented to</div>
          <h3 className="font-display text-4xl sm:text-5xl font-bold text-primary tracking-tight">
            {cert.recipient}
          </h3>
          <div className="text-sm text-muted-foreground mt-2">{cert.location}</div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex-1 h-px bg-border" />
          <Sparkles size={14} className={t.accent} />
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Description */}
        <p className="text-center text-muted-foreground leading-relaxed max-w-xl mx-auto mb-8 text-sm sm:text-base">
          For extraordinary contribution to community wellbeing — helping{" "}
          <span className="font-semibold text-foreground">{cert.personHelped}</span>.
          <br />
          <em className="text-muted-foreground/80">"{cert.story}"</em>
        </p>

        {/* Stats grid — 5 impact metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-10">
          {[
            { label: "People helped", value: cert.stats.livesTouched },
            { label: "Meals shared", value: cert.stats.meals },
            { label: "Volunteer hours", value: cert.stats.hours },
            { label: "Jobs created", value: cert.stats.jobsCreated },
            { label: "Impact score", value: cert.stats.impactScore },
          ].map((s, i) => (
            <div
              key={s.label}
              data-testid={`cert-stat-${i}`}
              className="rounded-2xl bg-accent/40 border border-border/50 p-4 text-center"
            >
              <div className={`font-display text-2xl sm:text-3xl font-bold ${t.accent}`}>
                {s.value.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-6 pt-6 border-t border-border/50">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Issued</div>
            <div className="font-display font-semibold text-foreground">{cert.dateIssued}</div>
          </div>

          <div className={`h-16 w-16 rounded-full ${t.seal} text-white flex items-center justify-center shrink-0 shadow-lg`}>
            <Award size={26} strokeWidth={2} />
          </div>

          <div className="text-right">
            <div className="text-xs text-muted-foreground uppercase tracking-widest flex items-center gap-1 justify-end">
              <ShieldCheck size={11} /> Verified
            </div>
            <div className="font-mono text-sm font-semibold text-foreground">{cert.verificationCode}</div>
          </div>
        </div>
      </div>

      {/* Bottom ribbon */}
      <div className={`h-2 w-full ${t.ribbon}`} />
    </div>
  );
}
